import { Button } from 'antd';
import cn from 'classnames';
import { useMemo } from 'react';

import { Select } from 'shared/components';

import {
  formatTickSizeForDisplay,
  generateTickSizeOptionsForExchange,
} from '../lib/generateTickSizeOptions';
import { OrderbookToolbarProps, OrderbookViewMode } from '../model/types';

import { All } from 'assets/images/orderbook/all';
import { Ask } from 'assets/images/orderbook/ask';
import { Bid } from 'assets/images/orderbook/bid';

export function OrderbookToolbar({
  tickSize,
  baseTickSize,
  lastPrice,
  viewMode,
  exchange,
  onTickSizeChange,
  onViewModeChange,
}: Omit<OrderbookToolbarProps, 'capabilities' | 'depth' | 'maxDepth' | 'onDepthChange'>) {
  const viewOptions: {
    icon: (props: { size?: number }) => JSX.Element;
    mode: OrderbookViewMode;
  }[] = useMemo(
    () => [
      { icon: All, mode: 'all' },
      { icon: Bid, mode: 'bid' },
      { icon: Ask, mode: 'ask' },
    ],
    [],
  );

  // Generate tick size options based on exchange, base tick size, and current price
  // Uses exchange-specific algorithm when available (e.g., WhiteBIT's multiplier pattern)
  // Falls back to generic smart algorithm for other exchanges
  const tickSizeOptions = useMemo(() => {
    if (!baseTickSize || baseTickSize <= 0) return [];

    const price = lastPrice ? parseFloat(lastPrice) : 0;
    return generateTickSizeOptionsForExchange({
      exchange,
      baseTickSize,
      currentPrice: price,
    });
  }, [exchange, baseTickSize, lastPrice]);

  const tickSizeItems = useMemo(
    () => tickSizeOptions.map(option => ({ id: option.toString(), value: option })),
    [tickSizeOptions],
  );

  const selectedTickItem = useMemo(() => {
    if (tickSize === null || tickSize === undefined) return undefined;
    return tickSizeItems.find(item => item.value === tickSize || item.id === tickSize.toString());
  }, [tickSize, tickSizeItems]);

  const handleTickSizeSelect = (
    value: { id: string; value: number } | { id: string; value: number }[] | null,
  ) => {
    // Single select - handle both single value and array for backwards compatibility
    const selected = Array.isArray(value) ? value[0] : value;
    onTickSizeChange(selected ? selected.value : null);
  };

  // Show tickSize selector for all exchanges when options are available
  const showTickSizeSelector = tickSizeItems.length > 0;

  return (
    <div className="flex w-full items-center gap-2 px-2 py-1.5 bg-zinc-900 border-b border-zinc-800">
      <Button.Group>
        {viewOptions.map(({ icon: Icon, mode }) => {
          const isActive = viewMode === mode;
          return (
            <Button
              key={mode}
              type="text"
              className={cn(
                '!flex !items-center !justify-center !w-8 !h-8 !p-0 !rounded-none first:!rounded-l-md last:!rounded-r-md !border',
                isActive
                  ? '!bg-zinc-800 !border-zinc-700 !text-white'
                  : '!bg-transparent !border-zinc-700 !text-zinc-400 hover:!text-white hover:!bg-zinc-800',
              )}
              onClick={() => onViewModeChange(mode)}
              title={`View ${mode}`}
            >
              <Icon size={20} />
            </Button>
          );
        })}
      </Button.Group>

      <div className="flex flex-1 justify-end gap-2">
        {showTickSizeSelector && (
          <div className="min-w-[140px]" title="Tick Size (Price Grouping)">
            <Select
              items={tickSizeItems}
              itemToString={item => formatTickSizeForDisplay(item.value)}
              placeholder="Tick size"
              value={selectedTickItem ?? null}
              onChange={handleTickSizeSelect}
            />
          </div>
        )}
      </div>
    </div>
  );
}
